/**
 * Cell — one board cell (ui/31 §2/§3). Purely presentational: shows the
 * occupant (via <BoardCard>) or a pending own-staged placement overlay, and
 * forwards clicks upward — `<GameView>` decides which Action (if any) a click
 * produces, since that depends on hand-selection/staged state it owns.
 */
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactElement } from 'react';
import { createPortal } from 'react-dom';
import type { AttackEligibility, CardInstance, CardType, CellId, EffectiveStats, PlayerId } from '@cards/engine';
import { BoardCard, statLen } from './BoardCard.js';
import { COLORS } from './kit/tokens.js';
import { cardDescription, cardName, cellDisplayLabel } from './locale.js';
import { CardGlyph } from './CardGlyph.js';
import { PixelSymbol } from './PixelIcon.js';
import { ScrollingCardName } from './ScrollingCardName.js';
import type { AttachmentView, CaptureReservationView } from './m14View.js';
import type { CardStatusView } from './cardStatus.js';
import type { PlayerNames } from './locale.js';
import type { PublicCellEffectBadgeView } from './locationEffectsModel.js';
import { CardInfoDialog, CardInfoPopover, type CardInfoData } from './CardInfoPopover.js';
import { flightOriginFromPreview, type FlightOrigin } from './cardFlight.js';
import { cardInfoDataFromDefinition } from './CardInfoCard.js';
import { CatalogCard } from './CatalogCard.js';
import { propsEqualByValue } from './memoEquality.js';
import { useBoardCarry } from './boardCarry.js';
import { invalidateDropZones, pinDropZoneRect, resolveDropCandidate } from './dropZones.js';
import {
  applyCardPointerMotion,
  cardDragMotionVariables,
  createCardDragMotion,
  liftCardDragMotionFromRect,
  previewAnchorGap,
  resetCardPointerMotion,
  setCardDragTarget,
  startCardDragAnimation,
  type CardDragMotion,
} from './cardMotion.js';
import { HandCardFace } from './HandCardFace.js';
import { CardBackFace } from './CardBackGlyph.js';
import { StatChip } from './StatChip.js';
import { useDesktopHover } from './useDesktopHover.js';
import { FULL_CARD_HOVER_OPEN_DELAY_MS, useHoverPopover } from './useHoverPopover.js';
import { useTurnPlaybackSelector } from './TurnPlayback.js';
import { Badge } from './kit/index.js';

/** Everything a staged pile card needs to draw its own face and preview. */
export interface StagedLayerCardView {
  instance: CardInstance;
  cardType: CardType | undefined;
  cost: number | undefined;
  hasStats: boolean;
  keywords: readonly string[];
}

export interface CellProps {
  cellId: CellId;
  instance: CardInstance | undefined;
  effective: EffectiveStats | undefined;
  cardType: CardType | undefined;
  keywords: readonly string[];
  /**
   * Whether a click on this cell may explain it instead of planning into it —
   * the keyboard-reachable half only (no hand selection, no targeting session).
   * A carried card suppresses details too, but through context, so that a pickup
   * does not change one prop on all 18 cells: see ./boardCarry.ts.
   */
  detailsEnabled: boolean;
  rush: boolean;
  shield: number;
  eligibility: AttackEligibility | undefined;
  controllerId: PlayerId | undefined;
  viewerId: PlayerId;
  playerNames: PlayerNames | undefined;
  attachments: readonly AttachmentView[];
  statuses: readonly CardStatusView[];
  /** Public, id-less badges authored by location-effect metadata. */
  effectBadges: readonly PublicCellEffectBadgeView[];
  reservation: CaptureReservationView | undefined;
  /** Own staged (not-yet-revealed) placement pending on this cell, if any. */
  stagedInstance: CardInstance | undefined;
  stagedCardType: CardType | undefined;
  stagedCardCost: number | undefined;
  stagedCardHasStats: boolean;
  stagedKeywords: readonly string[];
  stagedDragEnabled: boolean;
  /**
   * The FIELD intents buried under `stagedInstance` in this cell, bottom first.
   * They are drawn as full cards, not blank plates: each lies a whole step below
   * the next, so its name and stat row stay readable, and the player has to be
   * able to tell what is in the pile they are building. They also remain
   * measurable landing anchors for concurrent flights.
   */
  stagedLayerCards: readonly StagedLayerCardView[];
  /** A revealed FIELD intent shown briefly before its fizzle discard. */
  fizzleInstance: CardInstance | undefined;
  fizzleCardType: CardType | undefined;
  fizzleKeywords: readonly string[];
  fizzleLeaving: boolean;
  /** Keyboard-accessible removal of the top intent back to the hand. */
  onStagedKeyboardUnplan: () => void;
  /** Valid placement target for the currently selected hand card. */
  highlight: boolean;
  /** `canApply` rejected the placement here — dim, do not emit on click. */
  dimmed: boolean;
  /** Chosen as a target in the active board-targeting session. */
  targetSelected?: boolean;
  /** Playback-only location cue for effects that do not have a live card target. */
  playbackCue?: string;
  playbackCueDelta?: number;
  /** Low-contrast relative coordinate shown only while the cell is empty. */
  coordinateLabel: string;
  /** Pointer-drag target; false for opponent/occupied/invalid cells. */
  dropEnabled: boolean;
  /** Click/keyboard action is currently meaningful (place or unplace). */
  interactive: boolean;
  /** Responsive width supplied by the board layout. */
  width: string;
  onClick: () => void;
  onStagedPointerDragStart: () => void;
  onStagedPointerDrop: (clientX: number, clientY: number) => void;
  onStagedPointerDragEnd: () => void;
  /** Released staged card hands its preview to the shared flight system. */
  onStagedRelease: (motion: CardDragMotion, origin: FlightOrigin) => void;
  /** This cell's staged card is airborne — hide the overlay under the ghost. */
  stagedFlightHidden: boolean;
  /** Its flight parked — un-hide instantly beneath the dissolving ghost. */
  stagedFlightLanding: boolean;
  /** This cell's resolved occupant is the destination of an airborne ghost. */
  occupantFlightHidden: boolean;
  /** The occupant's ghost parked and is dissolving over the visible card. */
  occupantFlightLanding: boolean;
}

interface StagedPointerDrag {
  pointerId: number;
  startX: number;
  startY: number;
  /** Where the pointer was last seen: a cancelled carry lands there (03 §3.2). */
  lastX: number;
  lastY: number;
  motion: CardDragMotion;
  active: boolean;
}

/** The same pickup slop as a hand card's (POINTER_TAP_SLOP in Hand.tsx, 03 §3.2). */
const STAGED_DRAG_THRESHOLD = 8;
/** Where a carried staged card can be released: another valid cell, or back into the hand. */
const STAGED_DROP_ZONE_SELECTOR = '[data-drop-enabled="true"], [data-hand-drop-zone="true"]';

function CellComponent(props: CellProps): ReactElement {
  const {
    cellId: id,
    instance,
    effective,
    cardType,
    keywords,
    detailsEnabled,
    rush,
    shield,
    eligibility,
    controllerId,
    viewerId,
    playerNames,
    attachments,
    statuses,
    effectBadges,
    reservation,
    stagedInstance,
    stagedCardType,
    stagedCardCost,
    stagedCardHasStats,
    stagedKeywords,
    stagedDragEnabled,
    stagedLayerCards,
    fizzleInstance,
    fizzleCardType,
    fizzleKeywords,
    fizzleLeaving,
    onStagedKeyboardUnplan,
    highlight,
    dimmed,
    targetSelected = false,
    playbackCue,
    playbackCueDelta,
    coordinateLabel,
    dropEnabled,
    interactive,
    width,
    onClick,
    onStagedPointerDragStart,
    onStagedPointerDrop,
    onStagedPointerDragEnd,
    onStagedRelease,
    stagedFlightHidden,
    stagedFlightLanding,
    occupantFlightHidden,
    occupantFlightLanding,
  } = props;
  const desktopHover = useDesktopHover();
  // Read at event time only — a carry starting or ending does not re-render this
  // cell, which is exactly what it is for. See ./boardCarry.ts.
  const carry = useBoardCarry();
  // Round-start "everything face-down" lineup: this cell shows a back placeholder
  // until its card flips up. Subscribed per-cell so only lined-up cells re-render.
  const faceDownCount = useTurnPlaybackSelector((visuals) =>
    visuals.faceDownCells.filter((cellId) => cellId === id).length);
  // Where this cell stands in the deal: the backs land one after another, not
  // all on one frame (03 §4.2).
  const lineupOrder = useTurnPlaybackSelector((visuals) => Math.max(0, visuals.faceDownCells.indexOf(id)));
  const cellFaceDown = faceDownCount > 0;
  // Overlay host being swallowed: a ghost of the just-consumed host renders
  // above the receiver flipping up beneath it and is pulled into the card.
  const hostAbsorb = useTurnPlaybackSelector((visuals) =>
    visuals.hostAbsorbCue?.cellId === id ? visuals.hostAbsorbCue : undefined);
  // The beat before that: the meal is named while the host is still the real
  // occupant, so the ghost only takes over once there is something to eat.
  const hostExposing = hostAbsorb?.phase === 'expose';
  // Ordinary FIELD intents share the same card-on-card visual language as overlay
  // receivers: every card in a pile lies one full step above the one below it, so
  // the one below always shows its whole name and stat row. The pile therefore
  // grows as a mound and is NOT bounded by the cell — deep piles run up over the
  // row above, which is accepted. It used to divide one step between however many
  // layers there were, which kept the pile inside the cell but made a pile of
  // three unreadable (6px strips) and, worse, re-spaced every layer whenever the
  // depth changed: the whole stack shuffling each time a card was added or
  // revealed. A layer's step is its own index and nothing else now.
  // Layer 0 is the slot. A card in the slot (or a ghost of one being swallowed)
  // pushes the whole plan one layer up.
  const stagedBaseLayer = instance !== undefined || hostAbsorb !== undefined ? 1 : 0;
  // The pile as ONE list, bottom card first, top card last. Every member is the
  // same element with the same key, so pushing a card onto the pile — or pulling
  // one out of it — mounts or unmounts exactly the card that moved and leaves the
  // others' DOM nodes alone, sliding them over the 160ms top/bottom transition.
  // Drawing the top card as a separate sibling meant every push destroyed the
  // previous top card's subtree and re-created it as a blank plate one layer down:
  // a dropped frame on every drop, and neighbours whose offsets jumped without
  // animating because their node was new.
  const stagedPile: readonly StagedLayerCardView[] = stagedInstance === undefined
    ? []
    : [...stagedLayerCards, {
      instance: stagedInstance,
      cardType: stagedCardType,
      cost: stagedCardCost,
      hasStats: stagedCardHasStats,
      keywords: stagedKeywords,
    }];
  // The pile's FLOOR — is there a card in the slot the backs are lying on — is
  // derived live, every render, in BOTH directions: a card landing in the slot
  // mid-lineup lifts the pile off it, and the occupant dying mid-lineup (eaten by
  // a devourer, killed by a spell earlier in the queue) lets the pile settle back
  // down onto the bare slot. The floor used to be latched sticky-up, because
  // losing data-overlay-stacked also lost `position: absolute`, so a descent was
  // an instant ~10px snap mid-death. Now the geometry hangs off data-stack-layer,
  // which is always present (layer 0 collapses to a plain padded inset), and the
  // 160ms top/bottom transition carries the descent. Latching it instead left a
  // lone back hovering a full step above an emptied cell until its own reveal —
  // a card-shaped thing floating over nothing, read as a phantom card.
  const lineupLatchRef = useRef<{ cardType: CardType | undefined } | undefined>(undefined);
  if (faceDownCount === 0) {
    lineupLatchRef.current = undefined;
  } else if (lineupLatchRef.current === undefined) {
    // The type IS latched: stagedCardType is the VIEWER's own plan for this cell,
    // and it leaves the hand the moment it resolves — which repainted the frame of
    // every back still standing here, including the ones that belong to the
    // opponent.
    lineupLatchRef.current = { cardType: stagedCardType };
  }
  const lineupLatch = lineupLatchRef.current;
  const lineupBaseLayer = instance !== undefined || hostAbsorb !== undefined ? 1 : 0;
  // The pile lies on a card, so the backs are lifted off it and it stays readable
  // underneath. Only then may a back admit its type: see the render below.
  const lineupOnCard = faceDownCount > 0 && lineupBaseLayer > 0;
  const lineupCardType = lineupLatch?.cardType
    ?? (lineupOnCard ? ('UNIT' as const) : undefined);
  // Where the card now flipping up was lying one frame ago, as a layer index,
  // handed to the reveal keyframe through --cards-reveal-from so the flip STARTS
  // from the pile pose and travels into the slot as part of itself.
  // Without it the handoff was a teleport: the back unmounted at its offset and
  // the card mounted at the slot inset on the same frame — the "card jumps, comes
  // back, then reveals" twitch, worst when the pile stood on a corpse and sat
  // shifted through the whole explanation beat before the flip.
  // The departing back is always the topmost; with no pile at all this is 0, i.e.
  // a plain reveal is untouched.
  const enteringHere = useTurnPlaybackSelector((visuals) => {
    const entering = visuals.enteringId;
    if (entering === undefined) return undefined;
    // A denied card flips too, in the same spot the back it replaces stood.
    return entering === instance?.id || entering === fizzleInstance?.id ? entering : undefined;
  });
  // A card at zero health waiting out its death beat. Its shards are mounted
  // with it but stay invisible until the card's death animation starts them.
  const occupantFractured = useTurnPlaybackSelector((visuals) =>
    instance !== undefined && visuals.fracturedIds.includes(instance.id));
  const revealFromRef = useRef<{ layer: number; forId: string } | undefined>(undefined);
  const prevPileRef = useRef({ count: faceDownCount, base: lineupBaseLayer });
  if (faceDownCount < prevPileRef.current.count && enteringHere !== undefined) {
    revealFromRef.current = {
      // Measured against the PREVIOUS render's pile, not this one's: the card
      // flipping up becomes the occupant on the very frame its back unmounts, so
      // by the time this runs the floor has already moved under it.
      layer: prevPileRef.current.base + prevPileRef.current.count - 1,
      forId: enteringHere,
    };
  }
  prevPileRef.current = { count: faceDownCount, base: lineupBaseLayer };
  // Keyed on the card it was measured for: a later token or a second entry into
  // this cell must not inherit a stale offset.
  const revealFromLayer = revealFromRef.current !== undefined
    && revealFromRef.current.forId === enteringHere
    ? revealFromRef.current.layer
    : 0;
  // A card that wins the slot travels down into it, so its keyframe owns the
  // offset and ends at zero. A DENIED card never gets the slot: it flips where the
  // back stood and stays there until it fades, so its WRAPPER owns the offset for
  // good and the keyframe must start at zero — otherwise both apply and the card
  // lifts twice as far. The wrapper's layer is latched per card, because the
  // entering flag clears before the fade and the card may not drop as it goes.
  const fizzleStepRef = useRef<{ step: number; forId: string } | undefined>(undefined);
  if (fizzleInstance === undefined) {
    fizzleStepRef.current = undefined;
  } else if (fizzleStepRef.current?.forId !== fizzleInstance.id) {
    fizzleStepRef.current = {
      forId: fizzleInstance.id,
      // Where its own back was standing; failing that, one layer up if there is
      // anything under it at all.
      step: Math.max(revealFromLayer, instance !== undefined || faceDownCount > 0 ? 1 : 0),
    };
  }
  const fizzleStep = fizzleStepRef.current?.step ?? 0;
  const revealFrom = fizzleInstance !== undefined && enteringHere === fizzleInstance.id
    ? 0
    : revealFromLayer;
  // Anything standing above layer 0 hangs out over the cell's top edge, and the
  // cell isolates its own stacking context — so the pile paints wherever the CELL
  // paints, not wherever its own z-index says. On a stacked phone the front line
  // is raised over the still board (z-index 3, see KIT_CSS) to stay readable
  // through the glyph bleed of resting cards, and that put it straight across the
  // top of every pile that grew past its slot. Flagging the overhang lets exactly
  // those cells climb over the line, and only while they overhang; the flight
  // lifts stay above both because Board writes them as inline z-index.
  //
  // All four ways up out of the slot count, and `revealFrom` is the one that is
  // easy to miss: the flipping card's own back is already gone by then, so both
  // pile terms below read 0 while the keyframe still holds the card a full layer
  // high (see --cards-reveal-from in KIT_CSS). Miss it and the line cuts across
  // every reveal off a pile — the same bug, on the frames the player is watching.
  const stackOverhang = Math.max(
    stagedPile.length === 0 ? 0 : stagedBaseLayer + stagedPile.length - 1,
    faceDownCount === 0 ? 0 : lineupBaseLayer + faceDownCount - 1,
    fizzleStep,
    revealFrom,
  ) > 0;
  const {
    value: stagedHoverAnchor,
    closing: stagedHoverClosing,
    open: openStagedHover,
    cancelClose: cancelStagedHoverClose,
    scheduleClose: scheduleStagedHoverClose,
    closeNow: closeStagedHoverNow,
  } = useHoverPopover<HTMLElement>({ openDelayMs: FULL_CARD_HOVER_OPEN_DELAY_MS });
  const [stagedDetailsOpen, setStagedDetailsOpen] = useState(false);
  /** Which card of the pile the pointer is on: it slides its corner out. */
  const [stagedPeekId, setStagedPeekId] = useState<string | undefined>(undefined);
  /**
   * Which card the preview DESCRIBES. A ref, not state, because it must survive
   * the pointer leaving: the peek ends with the pointer (the card slides back
   * into the pile) while the preview stays up through its close grace and exit
   * fade, and re-deriving it from the live peek would flip the popover to the
   * top card's face for those last 300ms.
   */
  const stagedHoverCardRef = useRef<string | undefined>(undefined);
  const {
    value: cellHoverAnchor,
    closing: cellHoverClosing,
    open: openCellHover,
    cancelClose: cancelCellHoverClose,
    scheduleClose: scheduleCellHoverClose,
    closeNow: closeCellHoverNow,
  } = useHoverPopover<HTMLElement>({ openDelayMs: FULL_CARD_HOVER_OPEN_DELAY_MS });
  const [cellDetailsOpen, setCellDetailsOpen] = useState(false);
  const closeCellDetails = useCallback(() => setCellDetailsOpen(false), []);
  const [stagedPreview, setStagedPreview] = useState<{ motion: CardDragMotion } | undefined>(undefined);
  /** The pointer carries this staged card: the floating preview IS the card,
     so the source hides in place (still measurable for the release flight). */
  const [stagedCarried, setStagedCarried] = useState(false);
  const stagedRef = useRef<HTMLDivElement | null>(null);
  // Sticky per-cell flag: a staged card that arrived via a drag flight must not
  // replay its `cards-stage-land` bounce when the flight finishes and the
  // flight-state attributes clear (the flight already animated its entrance).
  // Stays set until the cell empties; click/keyboard placements never fly, so
  // the flag stays false for them and they keep the landing bounce.
  const stagedArrivedViaFlightRef = useRef(false);
  if (stagedInstance === undefined) {
    stagedArrivedViaFlightRef.current = false;
    // A carried card that left this cell (dropped elsewhere) must not leak its
    // hidden state onto the next staged card rendered here.
    if (stagedCarried) setStagedCarried(false);
    if (stagedPeekId !== undefined) setStagedPeekId(undefined);
  } else if (stagedFlightHidden || stagedFlightLanding) {
    stagedArrivedViaFlightRef.current = true;
  }
  const stagedPreviewRef = useRef<HTMLDivElement | null>(null);
  const stagedPointerRef = useRef<StagedPointerDrag | undefined>(undefined);
  const stagedDropTargetRef = useRef<HTMLElement | undefined>(undefined);
  const pendingStagedDropPointRef = useRef<{ clientX: number; clientY: number } | undefined>(undefined);
  const stagedDropFrameRef = useRef<number | undefined>(undefined);
  const suppressStagedClickRef = useRef(false);
  const closeStagedDetails = useCallback(() => setStagedDetailsOpen(false), []);

  function clearStagedDropTarget(): void {
    stagedDropTargetRef.current?.removeAttribute('data-pointer-drag-over');
    stagedDropTargetRef.current = undefined;
    pinDropZoneRect(undefined);
  }

  /**
   * Drops the queued hit test without touching the measurement, so the release
   * that follows resolves against the same boxes the highlight used. See the
   * matching helper in Hand.tsx.
   */
  function cancelStagedDropTargetUpdate(): void {
    pendingStagedDropPointRef.current = undefined;
    if (stagedDropFrameRef.current !== undefined) {
      globalThis.cancelAnimationFrame?.(stagedDropFrameRef.current);
      stagedDropFrameRef.current = undefined;
    }
  }

  function updateStagedDropTarget(clientX: number, clientY: number): void {
    const next = resolveDropCandidate(clientX, clientY)
      ?.closest<HTMLElement>(STAGED_DROP_ZONE_SELECTOR) ?? undefined;
    if (next === stagedDropTargetRef.current) return;
    clearStagedDropTarget();
    next?.setAttribute('data-pointer-drag-over', 'true');
    stagedDropTargetRef.current = next;
    pinDropZoneRect(next);
  }

  /**
   * Hit-test at the paint rate, not at the touch sensor's raw event rate. A
   * phone delivers pointermove at 120-240Hz, so resolving on the raw event ran
   * the hit test (and, before the shared resolver, a forced layout) several
   * times for a frame that can only paint one highlight.
   */
  function scheduleStagedDropTargetUpdate(clientX: number, clientY: number): void {
    pendingStagedDropPointRef.current = { clientX, clientY };
    if (stagedDropFrameRef.current !== undefined) return;
    if (globalThis.requestAnimationFrame === undefined) {
      pendingStagedDropPointRef.current = undefined;
      updateStagedDropTarget(clientX, clientY);
      return;
    }
    stagedDropFrameRef.current = globalThis.requestAnimationFrame(() => {
      stagedDropFrameRef.current = undefined;
      const pending = pendingStagedDropPointRef.current;
      pendingStagedDropPointRef.current = undefined;
      if (stagedPointerRef.current?.active !== true || pending === undefined) return;
      updateStagedDropTarget(pending.clientX, pending.clientY);
    });
  }
  // Cell-state preview: ONE combined card describes every location effect on
  // the cell — each effect is a state entry ("Склизь 2" + description). The
  // card's own definition description is suppressed so nothing repeats.
  const primaryBadge = effectBadges[0];
  const primaryDefinition = primaryBadge === undefined ? undefined : cardInfoDataFromDefinition(primaryBadge.definitionId);
  const cellStateCard: CardInfoData | undefined = primaryBadge === undefined ? undefined : {
    defId: primaryBadge.definitionId,
    ...(effectBadges.length > 1 ? { name: 'Эффекты ячейки' } : {}),
    description: '',
    hideGlyph: true,
    hideKeywords: true,
    ...(primaryDefinition?.type === undefined ? {} : { type: primaryDefinition.type }),
    statuses: effectBadges.map((badge) => ({
      id: badge.key,
      label: `${badge.label} ${badge.value}`,
      description: badge.description,
      color: COLORS.positive,
    })),
  };
  const cellDetailsAvailable = instance === undefined && stagedInstance === undefined
    && cellStateCard !== undefined && detailsEnabled && !interactive;
  const cellStateSummary = effectBadges.map((badge) => `${badge.label} ${badge.value}: ${badge.description}`).join(' ');

  useEffect(() => {
    if (!cellDetailsAvailable) {
      setCellDetailsOpen(false);
      closeCellHoverNow();
    }
  }, [cellDetailsAvailable, closeCellHoverNow]);
  // A carry does not re-render this cell (that is the whole point of the gate),
  // so what is already OPEN when one starts has to be closed from a listener
  // instead of from the effect above.
  useEffect(() => carry.subscribe(() => {
    if (!carry.carrying) return;
    setCellDetailsOpen(false);
    closeCellHoverNow();
    setStagedDetailsOpen(false);
    closeStagedHoverNow();
  }), [carry, closeCellHoverNow, closeStagedHoverNow]);
  // The preview follows the card under the pointer, not the top of the pile: a
  // buried card is only readable down to its stat row, so its full face is the
  // whole reason for hovering it. Falls back to the top card, which is what a
  // one-card cell has always shown.
  const stagedHoverCard = stagedPile.find(
    (card) => card.instance.id === stagedHoverCardRef.current,
  ) ?? stagedPile.at(-1);
  const stagedInfo: CardInfoData | undefined = stagedHoverCard === undefined ? undefined : {
    defId: stagedHoverCard.instance.defId,
    ...(stagedHoverCard.cardType === undefined ? {} : { type: stagedHoverCard.cardType }),
    ...(stagedHoverCard.cost === undefined ? {} : { cost: stagedHoverCard.cost }),
    ...(stagedHoverCard.hasStats ? {
      attack: stagedHoverCard.instance.stats.attack,
      health: stagedHoverCard.instance.stats.health,
      maxHealth: stagedHoverCard.instance.stats.maxHealth,
    } : {}),
    keywords: stagedHoverCard.keywords,
    statuses: [{ id: 'preparing', label: 'Подготовка', description: 'Карта выставлена и раскроется после готовности обоих игроков.', color: COLORS.energy }],
  };
  // The preview's testId names the card it DESCRIBES, so a test can tell the
  // buried card's preview from the top card's.
  const stagedInfoTestId = `staged-card-${stagedHoverCard?.instance.id ?? 'unknown'}`;

  /** Point at one card of the pile: it peeks out and the preview follows it. */
  function peekStagedCard(instanceId: string, anchor: HTMLElement): void {
    stagedHoverCardRef.current = instanceId;
    setStagedPeekId(instanceId);
    // Inside one pile the pointer is browsing, not deciding, so an already-open
    // preview switches card at once. Re-serving the 200ms dwell per card made it
    // blink off and on again as the pointer crossed from strip to strip.
    openStagedHover(anchor, stagedHoverAnchor !== undefined);
  }

  function handleStagedPointerDown(event: ReactPointerEvent<HTMLDivElement>): void {
    event.stopPropagation();
    if (!stagedDragEnabled || event.isPrimary === false || event.button !== 0) return;
    suppressStagedClickRef.current = false;
    cancelStagedDropTargetUpdate();
    clearStagedDropTarget();
    // Nothing is lifted at a gesture boundary — the only safe point to re-measure.
    invalidateDropZones();
    const rect = event.currentTarget.getBoundingClientRect();
    const originX = rect.width <= 0 ? 50 : (event.clientX - rect.left) / rect.width * 100;
    const originY = rect.height <= 0 ? 70 : (event.clientY - rect.top) / rect.height * 100;
    const motion = createCardDragMotion(event.clientX, event.clientY, -2, originX, originY);
    // When the preview materializes (first real move) it lifts out of the
    // staged slot instead of appearing at the above-the-finger anchor.
    liftCardDragMotionFromRect(motion, rect, previewAnchorGap());
    stagedPointerRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      motion,
      active: false,
    };
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // The drag still works while the pointer remains over the staged card.
    }
  }

  function handleStagedPointerMove(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = stagedPointerRef.current;
    if (drag === undefined || drag.pointerId !== event.pointerId) {
      // Как в боевой руке: на десктопе подготовленная карта наклоняется за
      // курсором, пока её не тянут.
      if (drag === undefined && desktopHover && event.pointerType !== 'touch') {
        applyCardPointerMotion(event.currentTarget, event.clientX, event.clientY);
      }
      return;
    }
    drag.lastX = event.clientX;
    drag.lastY = event.clientY;
    if (!drag.active && Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < STAGED_DRAG_THRESHOLD) return;
    if (!drag.active) {
      drag.active = true;
      suppressStagedClickRef.current = true;
      // Поднятая карта выравнивается: наклон дальше живёт на летящем превью.
      resetCardPointerMotion(event.currentTarget);
      closeStagedHoverNow();
      onStagedPointerDragStart();
      setStagedPreview({ motion: drag.motion });
      setStagedCarried(true);
    }
    event.preventDefault();
    event.stopPropagation();
    setCardDragTarget(drag.motion, event.clientX, event.clientY);
    scheduleStagedDropTargetUpdate(event.clientX, event.clientY);
  }

  function handleStagedPointerUp(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = stagedPointerRef.current;
    if (drag === undefined || drag.pointerId !== event.pointerId) return;
    stagedPointerRef.current = undefined;
    cancelStagedDropTargetUpdate();
    clearStagedDropTarget();
    setStagedPreview(undefined);
    setStagedCarried(false);
    if (drag.active) {
      event.preventDefault();
      event.stopPropagation();
      const origin = flightOriginFromPreview(stagedPreviewRef.current, drag.motion);
      onStagedPointerDrop(event.clientX, event.clientY);
      // Released like a hand card: the ghost flies to wherever the card now
      // lives — its (old or new) staged cell, or the hand slot after unplan.
      onStagedRelease(drag.motion, origin);
    }
  }

  function handleStagedPointerCancel(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = stagedPointerRef.current;
    if (drag === undefined || drag.pointerId !== event.pointerId) return;
    stagedPointerRef.current = undefined;
    cancelStagedDropTargetUpdate();
    clearStagedDropTarget();
    // A carry the browser took away (it decided the finger was scrolling) is
    // not abandoned, exactly as in the hand: the card lands where the finger
    // was last seen — on a valid target there, or back where it came from.
    if (drag.active) {
      const origin = flightOriginFromPreview(stagedPreviewRef.current, drag.motion);
      setStagedPreview(undefined);
      setStagedCarried(false);
      onStagedPointerDrop(drag.lastX, drag.lastY);
      onStagedRelease(drag.motion, origin);
      onStagedPointerDragEnd();
      return;
    }
    setStagedPreview(undefined);
    setStagedCarried(false);
  }

  useEffect(() => {
    const element = stagedPreviewRef.current;
    if (stagedPreview === undefined || element === null) return;
    const motion = stagedPreview.motion;
    return startCardDragAnimation(element, () =>
      stagedPointerRef.current?.motion === motion ? motion : undefined);
  }, [stagedPreview]);

  return (
    <>
      <div
      className="cards-board-cell"
      data-testid={`cell-${id}`}
      data-cell-id={id}
      data-empty={instance === undefined && stagedInstance === undefined ? 'true' : undefined}
      data-highlight={highlight ? 'true' : undefined}
      data-target-selected={targetSelected ? 'true' : undefined}
      data-playback-cue={playbackCue}
      data-playback-cue-delta={playbackCueDelta}
      data-dimmed={dimmed ? 'true' : undefined}
      data-drop-enabled={dropEnabled ? 'true' : undefined}
      data-stack-overhang={stackOverhang ? 'true' : undefined}
      data-interactive={interactive ? 'true' : undefined}
      // The five attributes below used to blink out for the length of every drag:
      // the carry was half of detailsEnabled, hence of cellDetailsAvailable. An idle
      // cell now keeps them (a droppable one always kept role and tabIndex through
      // interactive), and that is deliberate: a pointer drag is not a reason to
      // rewrite the accessibility tree of eighteen cells. The handlers below refuse
      // the press on their own, so aria-haspopup briefly promises a dialog that
      // would not open — and the one person that could notice, a screen-reader user,
      // is not the one holding the pointer down.
      role={interactive || cellDetailsAvailable ? 'button' : undefined}
      tabIndex={interactive || cellDetailsAvailable ? 0 : undefined}
      aria-haspopup={!interactive && cellDetailsAvailable ? 'dialog' : undefined}
      aria-label={
        interactive
          ? `Выложить выбранную карту: ${cellDisplayLabel(id)}`
          : cellDetailsAvailable
            ? `Состояние ячейки ${coordinateLabel}. ${cellStateSummary}`
            : undefined
      }
      title={!interactive && cellDetailsAvailable && !desktopHover ? cellStateSummary : undefined}
      onClick={() => {
        // Between them, detailsEnabled (no selection, no targeting) and the carry
        // gate guarantee no planning gesture is in flight, so hijacking the click
        // for the cell-state dialog never hides a planning click — those all flow
        // through the plain onClick branch.
        if (cellDetailsAvailable && !carry.carrying) {
          if (!desktopHover) setCellDetailsOpen(true);
          return;
        }
        onClick();
      }}
      onKeyDown={(event) => {
        // I opens the cell's state from the keyboard (Q6); Enter/Space act.
        if (event.code === 'KeyI' && !event.ctrlKey && !event.metaKey && !event.altKey) {
          if (cellDetailsAvailable && !carry.carrying) {
            event.preventDefault();
            setCellDetailsOpen(true);
          }
          return;
        }
        if (event.key !== 'Enter' && event.key !== ' ') return;
        if (interactive) {
          event.preventDefault();
          onClick();
        } else if (cellDetailsAvailable && !carry.carrying) {
          // The same gate the click uses. Reaching this mid-carry takes a key press
          // during a pointer drag, so it is close to unreachable — but the two paths
          // opening the same dialog must not disagree about when it may open.
          event.preventDefault();
          setCellDetailsOpen(true);
        }
      }}
      onMouseEnter={(event) => {
        if (!interactive && cellDetailsAvailable && !carry.carrying && desktopHover) openCellHover(event.currentTarget);
      }}
      onMouseLeave={() => scheduleCellHoverClose()}
      // The board owns responsive cell geometry; presentation and interaction
      // states are expressed through kit classes/data attributes.
      // --cards-reveal-from rides the cell rather than the card because the
      // entering card's data-animation is derived inside BoardCard; inheritance
      // gets it to the keyframe without threading a prop through. The fizzle step
      // travels the same way, to keep this the cell's only style block.
      style={{
        '--cards-cell-instance-width': width,
        '--cards-reveal-from': revealFrom,
        '--cards-fizzle-step': fizzleStep,
      } as CSSProperties}
    >
      {Array.from({ length: faceDownCount }, (_, index) => {
        // Reverse deal: index 0 is the front of the queue and therefore the TOP
        // of the pile, index last lies in the slot. So the card about to flip is
        // never buried and nothing has to be pulled out from under anything.
        // Key by DEPTH — how far off the bottom of the pile this back lies, which
        // is what survives the top one leaving (index i+1 of N becomes index i of
        // N-1 at the same depth). Never by the layer: the layer also carries the
        // FLOOR, which moves under the whole pile when a card lands in the slot or
        // is eaten out of it, and folding that into the key remounted every back
        // that was standing there — the pile teleported a full step instead of
        // sliding over its 160ms transition, right as the next card flipped up.
        const depth = faceDownCount - 1 - index;
        const layer = lineupBaseLayer + depth;
        return (
          <div
            key={`facedown-${id}-${depth}`}
            className="cards-board-card cards-board-card-lineup"
            // On the topmost back: the one that reveals next, which is what the
            // pile's geometry is asserted through.
            data-testid={index === 0 ? `facedown-${id}` : undefined}
            // Marks it as lying on something, which is what earns the dropped
            // shadow; the geometry keys on data-stack-layer instead, so it never
            // switches positioning mode.
            data-overlay-stacked={layer > 0 ? 'true' : undefined}
            data-stack-layer={layer}
            // A plain lineup back stays type-less: the type is hidden information.
            // An overlay one hides nothing — only units can be played onto a card,
            // and a back lying on an occupant already says that is what this is.
            // Stating it keeps the face identical to the card that takes over from
            // this back (the card skin is chosen by type), so the handoff frame
            // does not change the cover.
            {...(lineupCardType === undefined ? {} : { 'data-card-type': lineupCardType })}
            aria-hidden="true"
            style={{ '--cards-stack-step': layer, '--cards-lineup-order': lineupOrder } as CSSProperties}
          >
            <CardBackFace patternId={`facedown-${id}-${depth}-back-pattern`} />
          </div>
        );
      })}
      {/* The occupant never yields the cell to a card played on top of it — not
         to the plan, and not to the lineup back that replaces the plan at the
         reveal (both lie on it, shifted up off its name and stat strip). So the
         card being eaten is on screen — and readable — from the moment something
         lands on it, and the flip that eats it is the first surprise of the
         beat. */}
      {(!cellFaceDown || lineupOnCard) && instance !== undefined && effective !== undefined ? (
        <BoardCard
          instance={instance}
          effective={effective}
          {...(cardType === undefined ? {} : { cardType })}
          keywords={keywords}
          detailsEnabled={detailsEnabled}
          rush={rush}
          shield={shield}
          eligibility={eligibility ?? 'not_unit'}
          controllerId={controllerId ?? instance.ownerId}
          viewerId={viewerId}
          playerNames={playerNames}
          attachments={attachments}
          statuses={statuses}
          {...(cellStateCard === undefined ? {} : { cellStateCard })}
          flightHidden={occupantFlightHidden}
          flightLanding={occupantFlightLanding}
        />
      ) : null}
      {/* The two effect layers below sit AFTER the card on purpose: they are
         started by `.cards-board-card[...] ~ .cards-death-shards` style sibling
         rules, so the card's own animation state is the only trigger and no
         timing has to be duplicated here. Both stay inside the card's box —
         the location clips anything that strays past it. */}
      {occupantFractured && instance !== undefined ? (
        <span className="cards-death-shards" data-card-type={cardType} aria-hidden="true">
          <span /><span /><span /><span /><span /><span /><span /><span /><span /><span />
        </span>
      ) : null}
      {instance !== undefined && enteringHere === instance.id ? (
        <span className="cards-reveal-dust" aria-hidden="true">
          <span /><span /><span /><span /><span /><span />
        </span>
      ) : null}
      {!cellFaceDown && fizzleInstance === undefined
        ? stagedPile.map((card, index) => {
          const top = index === stagedPile.length - 1;
          const layer = stagedBaseLayer + index;
          const name = cardName(card.instance.defId);
          return (
          // Keyed by the card it shows, top and buried alike: the pile is one
          // keyed list, so a card gaining or losing the top spot keeps its DOM
          // node (only its attributes change) and the cards around it animate to
          // their new layer instead of being re-created there. Unkeyed, React
          // reused one node per position — the same element slid a layer while
          // its art and name swapped, so card one appeared to morph into card
          // two instead of card two landing on it.
            <div
              key={card.instance.id}
              {...(top ? { ref: stagedRef } : {})}
              // Buried cards are ordinary staged cards, drawn in full, wearing one
              // extra class for the geometry they no longer share with the top one.
              className={top ? 'cards-staged-card' : 'cards-staged-card cards-staged-stack-layer'}
              data-testid={`staged-card-${card.instance.id}`}
              data-preparing="true"
              data-stack-layer={layer}
              data-overlay-stacked={layer > 0 ? 'true' : undefined}
              data-buried={top ? undefined : 'true'}
              // Pointed at: slides its corner out of the pile (see KIT_CSS). Only
              // ever a buried card — the top one is already fully visible.
              data-peek={!top && stagedPeekId === card.instance.id ? 'true' : undefined}
              data-carried={top && stagedCarried ? 'true' : undefined}
              data-flight-hidden={top && stagedFlightHidden && !stagedFlightLanding ? 'true' : undefined}
              data-flight-landing={top && stagedFlightLanding ? 'true' : undefined}
              data-flight-landed={top && stagedArrivedViaFlightRef.current ? 'true' : undefined}
              data-card-type={card.cardType}
              data-drag-enabled={top && stagedDragEnabled ? 'true' : undefined}
              data-flight-staged-id={card.instance.id}
              style={{ '--cards-stack-step': layer } as CSSProperties}
              draggable={false}
              role="button"
              tabIndex={0}
              aria-keyshortcuts={top && stagedDragEnabled ? 'Delete Backspace' : undefined}
              aria-haspopup={interactive ? undefined : 'dialog'}
              aria-label={interactive
                ? `Выложить выбранную карту: ${cellDisplayLabel(id)}`
                : top
                  ? stagedPile.length > 1
                    ? `Стопка подготовленных карт. Верхняя карта: ${name}. Перетащите её или нажмите Delete, чтобы вернуть в руку.`
                    : `Описание карты ${name}. Перетащите в другую ячейку или нажмите Delete, чтобы вернуть в руку.`
                  : `Описание карты ${name}, ${stagedPile.length - 1 - index} ${stagedPile.length - index === 2 ? 'картой' : 'картами'} ниже верха стопки.`}
              title={desktopHover ? undefined : `${name} — ${cardDescription(card.instance.defId) ?? 'Описание отсутствует'}`}
              onMouseEnter={(event) => {
                if (desktopHover) peekStagedCard(card.instance.id, event.currentTarget);
              }}
              onMouseLeave={(event) => {
                if (top) resetCardPointerMotion(event.currentTarget);
                if (stagedPeekId === card.instance.id) setStagedPeekId(undefined);
                scheduleStagedHoverClose();
              }}
              onClick={(event) => {
                event.stopPropagation();
                if (interactive) {
                  onClick();
                  return;
                }
                if (top && suppressStagedClickRef.current && event.detail !== 0) {
                  suppressStagedClickRef.current = false;
                  event.preventDefault();
                  return;
                }
                suppressStagedClickRef.current = false;
                // Without hover there is no preview, so the tap opens the sheet —
                // for the card that was tapped, buried or not.
                stagedHoverCardRef.current = card.instance.id;
                if (!desktopHover) setStagedDetailsOpen(true);
              }}
              onKeyDown={(event) => {
                if ((event.key === 'Delete' || event.key === 'Backspace') && top && stagedDragEnabled) {
                  event.preventDefault();
                  event.stopPropagation();
                  onStagedKeyboardUnplan();
                  return;
                }
                if (event.code === 'KeyI' && !event.ctrlKey && !event.metaKey && !event.altKey) {
                  event.preventDefault();
                  event.stopPropagation();
                  stagedHoverCardRef.current = card.instance.id;
                  setStagedDetailsOpen(true);
                  return;
                }
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  if (interactive) {
                    onClick();
                    return;
                  }
                  stagedHoverCardRef.current = card.instance.id;
                  setStagedDetailsOpen(true);
                }
              }}
              // Only the top card of a pile is draggable; a buried one lets the
              // press through to the cell, which is what carries a placement.
              {...(top ? {
                onPointerDown: handleStagedPointerDown,
                onPointerMove: handleStagedPointerMove,
                onPointerUp: handleStagedPointerUp,
                onPointerCancel: (event: ReactPointerEvent<HTMLDivElement>) => {
                  resetCardPointerMotion(event.currentTarget);
                  handleStagedPointerCancel(event);
                },
                onLostPointerCapture: (event: ReactPointerEvent<HTMLDivElement>) => {
                  resetCardPointerMotion(event.currentTarget);
                  handleStagedPointerCancel(event);
                },
              } : {})}
            >
              {/* Same glyph size as the revealed BoardCard so the reveal reads
                 as this staged card resolving in place, not a different, larger card
                 popping in — the size discontinuity was the visible "resize" jump. */}
              <div className="cards-staged-card-art" aria-hidden="true">
                <CardGlyph defId={card.instance.defId} size={60} />
              </div>
              {/* Same name row as the revealed BoardCard so the reveal (and the
                 flight ghost's landing) keeps the whole face pixel-identical. */}
              <ScrollingCardName
                name={name}
                className="cards-board-card-name"
                trackClassName="cards-board-card-name-track"
              />
              {card.hasStats ? (
                <div className="cards-staged-card-stats">
                  <StatChip
                    icon="attack"
                    value={card.instance.stats.attack}
                    len={statLen(card.instance.stats.attack)}
                    aria-label={`Атака: ${card.instance.stats.attack}`}
                    title={`Атака: ${card.instance.stats.attack}`}
                  />
                  <StatChip
                    icon="health"
                    value={card.instance.stats.health}
                    len={statLen(card.instance.stats.health)}
                    aria-label={`Здоровье: ${card.instance.stats.health}`}
                    title={`Здоровье: ${card.instance.stats.health}`}
                  />
                </div>
              ) : null}
            </div>
          );
        })
        : null}
      {fizzleInstance !== undefined ? (
        <div
          className="cards-field-fizzle-card"
          data-testid={`field-fizzle-${fizzleInstance.id}`}
          // The wrapper carries both the pile pose (--cards-fizzle-step, off the
          // cell) and the exit; the card inside owns its own transform through the
          // reveal flip that put it here.
          data-leaving={fizzleLeaving ? 'true' : undefined}
        >
          <BoardCard
            instance={fizzleInstance}
            effective={{
              attack: fizzleInstance.stats.attack,
              health: fizzleInstance.stats.health,
              maxHealth: fizzleInstance.stats.maxHealth,
            }}
            {...(fizzleCardType === undefined ? {} : { cardType: fizzleCardType })}
            keywords={fizzleKeywords}
            detailsEnabled={false}
            rush={fizzleKeywords.includes('RUSH')}
            shield={0}
            eligibility="not_unit"
            controllerId={fizzleInstance.controllerId}
            viewerId={viewerId}
            playerNames={playerNames}
          />
        </div>
      ) : null}
      {!cellFaceDown && instance === undefined && stagedInstance === undefined ? (
        <span className="cards-board-cell-empty-mark" aria-hidden="true">+</span>
      ) : null}
      {hostAbsorb !== undefined && !hostExposing ? (
        <div
          className="cards-board-card cards-overlay-host-ghost"
          data-testid={`host-ghost-${id}`}
          data-card-type="UNIT"
          aria-hidden="true"
        >
          <div className="cards-board-card-art" aria-hidden="true">
            <CardGlyph defId={hostAbsorb.defId} size={60} />
          </div>
          <ScrollingCardName
            name={cardName(hostAbsorb.defId)}
            className="cards-board-card-name"
            trackClassName="cards-board-card-name-track"
          />
          {/* Same stat strip, same offsets as the BoardCard this ghost takes
             over from (see cards-board-card-stats): the swap happens on a
             single frame, and a host that dropped its numbers on that frame
             read as a glitch rather than as the same card being eaten. */}
          {hostAbsorb.stats !== undefined ? (
            <div className="cards-board-card-stats">
              <StatChip icon="attack" value={hostAbsorb.stats.attack} len={statLen(hostAbsorb.stats.attack)} />
              <StatChip icon="health" value={hostAbsorb.stats.health} len={statLen(hostAbsorb.stats.health)} />
            </div>
          ) : null}
        </div>
      ) : null}
      {/* What the swallow moved, floated out of the gulp. A sibling of the
         ghost rather than a child: the ghost is being crushed to nothing, and
         numbers riding that transform would be unreadable. */}
      {hostAbsorb?.gained !== undefined ? (
        <div
          className="cards-overlay-host-gain"
          data-testid={`host-gain-${id}`}
          aria-hidden="true"
        >
          {hostAbsorb.gained.attack > 0 ? (
            <StatChip icon="attack" value={`+${hostAbsorb.gained.attack}`} />
          ) : null}
          {hostAbsorb.gained.health > 0 ? (
            <StatChip icon="health" value={`+${hostAbsorb.gained.health}`} />
          ) : null}
        </div>
      ) : null}
      {reservation !== undefined ? (
        <Badge
          tone="warning"
          className="cards-cell-reservation"
          data-testid={`capture-reservation-${id}`}
          data-has-effect-badges={effectBadges.length > 0 ? 'true' : undefined}
          title={reservation.sourceCellId === id ? 'Исходная ячейка временно зарезервирована для возврата' : 'Ячейка назначения захваченной карты'}
        >
          РЕЗЕРВ
        </Badge>
      ) : null}
      {effectBadges.length > 0 ? (
        <div className="cards-cell-effect-badges">
          {effectBadges.map((badge, index) => (
            <Badge
              key={badge.key}
              tone="success"
              className="cards-cell-effect-badge"
              data-testid={`cell-${id}-effect-badge-${index}`}
              aria-label={`${badge.label}: ${badge.value}`}
              title={`${badge.label} ${badge.value}: ${badge.description}`}
            >
              <span aria-hidden="true"><PixelSymbol symbol={badge.symbol} /></span>{badge.value}
            </Badge>
          ))}
        </div>
      ) : null}
      </div>
      {stagedHoverAnchor !== undefined && desktopHover && stagedInfo !== undefined ? (
        <CardInfoPopover
          anchor={stagedHoverAnchor}
          data={stagedInfo}
          closing={stagedHoverClosing}
          testId={`${stagedInfoTestId}-popover`}
          onMouseEnter={cancelStagedHoverClose}
          onMouseLeave={scheduleStagedHoverClose}
        />
      ) : null}
      {stagedDetailsOpen && stagedInfo !== undefined ? (
        <CardInfoDialog
          data={stagedInfo}
          testId={`${stagedInfoTestId}-detail`}
          onClose={closeStagedDetails}
          {...(cellStateCard === undefined ? {} : {
            supplement: <CatalogCard data={cellStateCard} testId={`${stagedInfoTestId}-detail-effects`} />,
          })}
        />
      ) : null}
      {cellHoverAnchor !== undefined && desktopHover && cellDetailsAvailable && cellStateCard !== undefined ? (
        <CardInfoPopover
          anchor={cellHoverAnchor}
          data={cellStateCard}
          closing={cellHoverClosing}
          testId={`cell-${id}-state-popover`}
          onMouseEnter={cancelCellHoverClose}
          onMouseLeave={scheduleCellHoverClose}
        />
      ) : null}
      {cellDetailsOpen && cellStateCard !== undefined ? (
        <CardInfoDialog data={cellStateCard} testId={`cell-${id}-state-detail`} onClose={closeCellDetails} />
      ) : null}
      {stagedPreview !== undefined && stagedInstance !== undefined && typeof document !== 'undefined' ? createPortal(
        <div
          ref={stagedPreviewRef}
          className="cards-touch-drag-preview"
          data-testid="staged-touch-drag-preview"
          data-card-type={stagedCardType}
          aria-hidden="true"
          // Pointer coordinates are the only per-frame dynamic style; the
          // position rides --cards-drag-x/y in the transform chain.
          style={cardDragMotionVariables(stagedPreview.motion) as CSSProperties}
        >
          <HandCardFace
            defId={stagedInstance.defId}
            cost={stagedCardCost}
            type={stagedCardType}
            hasStats={stagedCardHasStats}
            attack={stagedInstance.stats.attack}
            health={stagedInstance.stats.health}
            nameAnimated={false}
          />
        </div>,
        document.body,
      ) : null}
    </>
  );
}

export const Cell = memo(CellComponent, propsEqualByValue);
